<script setup lang="ts">
import { computed, markRaw, onMounted, onUnmounted, ref, watch } from 'vue';
import {
	PiButton,
	PiInputField,
	PiMessage,
	PiSpinner,
	PiIconSolidAngleLeft,
	PiIconSolidArrowsRotate,
	PiIconSolidEllipsisVertical,
	PiIconSolidList,
	PiIconSolidArrowUpRightFromSquare,
	PiIconRegularFileLines,
	PiIconSolidSort,
	PiIconSolidCheck,
	PiChip,
	PiDropdown,
	PiDropdownItem,
	PiChipDropdown
} from '@pappers-fr/pappers-ui';
import type { ChipOptions } from '@pappers-fr/pappers-ui/dist/src/components/PIChipDropdown/types.js';
import type { PieceSort } from '@/types';
import { useFoldersStore } from '@/stores/folders';
import { usePiecesStore } from '@/stores/pieces';
import { useWorkspaceApi } from '@/composables/useWorkspaceApi';
import { useWordDocument } from '@/composables/useWordDocument';
import PieceCard from './PieceCard.vue';

const foldersStore = useFoldersStore();
const piecesStore = usePiecesStore();
const { fetchPieces, fetchTags, generateStatementOfFacts } = useWorkspaceApi();
const { insertPiece, insertBordereau, insertStatementOfFacts, scrollToPiece, detectDeletedControls, renumber } =
	useWordDocument();

const isGenerating = ref(false);
const isActionsOpen = ref(false);
const isSortOpen = ref(false);

const folder = computed(() => foldersStore.selectedFolder!);
const hasDocuments = computed(() => folder.value.documents > 0);

const exportUrl = computed(() => {
	const f = folder.value;
	return `https://justice.pappers.fr/mon-compte/affaires/${f.organizationId}/${f.id}?export`;
});

const folderUrl = computed(() => {
	const f = folder.value;
	return `https://justice.pappers.fr/mon-compte/affaires/${f.organizationId}/${f.id}`;
});

const addPiecesUrl = computed(() => {
	const f = folder.value;
	return `https://justice.pappers.fr/mon-compte/affaires/${f.organizationId}/${f.id}/import`;
});

const tagOptions = computed<ChipOptions>(() => {
	const base: ChipOptions = [
		{ label: 'Tous', value: '', color: 'surface' },
		{ label: 'Client', value: 'Client', color: 'green' },
		{ label: 'Adversaire', value: 'Adversaire', color: 'red' }
	];
	const custom = piecesStore.availableTags
		.filter((t) => t !== 'Client' && t !== 'Adversaire')
		.map((t) => ({ label: t, value: t, color: 'purple' as const }));
	return [...base, ...custom];
});

const sortOptions: { label: string; value: PieceSort }[] = [
	{ label: 'Date (plus récentes)', value: 'date-desc' },
	{ label: 'Date (plus anciennes)', value: 'date-asc' },
	{ label: 'Numéro de pièce', value: 'number' }
];

const sortLabel = computed(() => sortOptions.find((o) => o.value === piecesStore.sortBy)?.label ?? '');

const handleSortChange = (value: PieceSort) => {
	piecesStore.sortBy = value;
	isSortOpen.value = false;
};

const loadTags = async () => {
	try {
		piecesStore.availableTags = await fetchTags(folder.value.id);
	} catch {
		/* non bloquant */
	}
};

const loadAllPieces = async () => {
	const all = await fetchPieces(folder.value.id);
	piecesStore.setAllPieces(all);
};

const loadPieces = async (query?: string, tag?: string) => {
	piecesStore.isLoading = true;
	piecesStore.error = null;
	try {
		const pieces = await fetchPieces(folder.value.id, query || undefined, tag || piecesStore.selectedTag || undefined);
		piecesStore.setPieces(pieces);
		// Recharger toutes les pièces (sans filtre) pour avoir les numéros réservés à jour
		await loadAllPieces();
		// Recalculer les numéros word en cas de changement des numéros Pappers
		// ! Pas de re-calcul des numéros Word a l'ouverture pour eviter que toutes les pieces soient exclues du bordereau
		// ! -- a l'ouverture d'un dossier dans un document vide.
		// await renumber();
	} catch (e: any) {
		piecesStore.error = e.message;
	} finally {
		piecesStore.isLoading = false;
	}
};

let searchTimeout: ReturnType<typeof setTimeout> | null = null;

watch(
	() => piecesStore.searchQuery,
	(query) => {
		if (searchTimeout) clearTimeout(searchTimeout);
		searchTimeout = setTimeout(() => loadPieces(query), 300);
	}
);

watch(
	() => piecesStore.selectedTag,
	() => {
		loadPieces(piecesStore.searchQuery);
	}
);

const handleBack = () => {
	foldersStore.clearSelection();
	piecesStore.setPieces([]);
	piecesStore.searchQuery = '';
};

const handleInsertPiece = async (piece: any) => {
	try {
		await insertPiece(piece);
	} catch (e: any) {
		let debugInfo = '';
		try {
			debugInfo = e?.debugInfo ? JSON.stringify(e.debugInfo) : '';
		} catch {
			debugInfo = '[non sérialisable]';
		}
		const code = e?.code || '';
		const name = e?.name || '';
		const stack = e?.stack || '';
		piecesStore.error = `Erreur d'insertion: ${name}: ${e.message}${code ? ` (code: ${code})` : ''}${debugInfo ? ` | debug: ${debugInfo}` : ''} | stack: ${stack}`;
	}
};

const handleScrollToPiece = async (pieceId: string) => {
	if (piecesStore.isInserted(pieceId)) {
		await scrollToPiece(pieceId);
	}
};

const handleInsertBordereau = async () => {
	try {
		await insertBordereau();
	} catch (e: any) {
		piecesStore.error = `Erreur bordereau: ${e.message}`;
	}
};

const handleInsertStatementOfFacts = async () => {
	isGenerating.value = true;
	piecesStore.error = null;
	try {
		const text = await generateStatementOfFacts(folder.value.id);
		await insertStatementOfFacts(text);
	} catch (e: any) {
		piecesStore.error = `Erreur rappel des faits: ${e.message}`;
	} finally {
		isGenerating.value = false;
	}
};

const openExternal = (url: string) => {
	window.open(url, '_blank');
};

let detectInterval: ReturnType<typeof setInterval> | null = null;

onMounted(async () => {
	loadTags();
	// Charger toutes les pièces (sans filtre) d'abord pour avoir les numéros réservés
	await loadAllPieces();
	loadPieces();
	detectInterval = setInterval(detectDeletedControls, 3000);
});

onUnmounted(() => {
	if (detectInterval) clearInterval(detectInterval);
});
</script>

<template>
	<div class="flex flex-col h-full">
		<!-- Header -->
		<div class="flex items-center gap-2 p-3 border-b border-neutral-100">
			<PiButton type="ghost" color="muted" size="sm" :icon="markRaw(PiIconSolidAngleLeft)" @click="handleBack" />
			<h2 class="text-sm font-semibold text-neutral-800 truncate flex-1">{{ folder.name }}</h2>
			<PiButton
				type="ghost"
				color="primary"
				size="sm"
				:icon="markRaw(PiIconSolidArrowsRotate)"
				title="Recharger"
				@click="loadPieces()"
			/>
			<PiDropdown v-model="isActionsOpen">
				<template #trigger>
					<PiButton type="ghost" color="primary" size="sm" :icon="markRaw(PiIconSolidEllipsisVertical)" />
				</template>
				<div class="flex flex-col gap-xs p-xs min-w-[220px] text-[11px]">
					<PiDropdownItem
						label="Voir le dossier sur Pappers"
						:left-icon="markRaw(PiIconSolidArrowUpRightFromSquare)"
						@click="openExternal(folderUrl)"
					/>
					<PiDropdownItem
						label="Ajouter des pièces"
						:left-icon="markRaw(PiIconSolidArrowUpRightFromSquare)"
						@click="openExternal(addPiecesUrl)"
					/>
					<PiDropdownItem
						label="Exporter le dossier"
						:left-icon="markRaw(PiIconSolidArrowUpRightFromSquare)"
						@click="openExternal(exportUrl)"
					/>
					<PiDropdownItem
						label="Recharger le dossier"
						:left-icon="markRaw(PiIconSolidArrowsRotate)"
						@click="loadPieces()"
					/>
				</div>
			</PiDropdown>
		</div>

		<!-- Actions -->
		<div class="flex flex-wrap gap-2 px-3 py-2 border-b border-neutral-100">
			<PiButton
				label="Bordereau"
				color="primary"
				size="sm"
				:icon="markRaw(PiIconSolidList)"
				:is-disabled="!hasDocuments"
				@click="handleInsertBordereau"
			/>
			<PiButton
				label="Rappel des faits"
				type="outline"
				color="primary"
				size="sm"
				:icon="markRaw(PiIconRegularFileLines)"
				:is-disabled="!hasDocuments"
				@click="handleInsertStatementOfFacts"
			/>
		</div>

		<!-- Recherche + tag + tri -->
		<div class="flex flex-col gap-2 p-3">
			<PiInputField
				:model-value="piecesStore.searchQuery"
				placeholder="Rechercher une pièce..."
				size="sm"
				@update:model-value="piecesStore.searchQuery = $event"
			/>
			<div class="flex items-center gap-2">
				<PiChipDropdown
					v-model="piecesStore.selectedTag"
					:options="tagOptions"
					chip-items
					has-chevron
					close-on-click
					header="Filtrer par tag"
				/>
				<PiDropdown v-model="isSortOpen">
					<template #trigger>
						<PiChip :icon="markRaw(PiIconSolidSort)" :label="sortLabel" color="surface" class="cursor-pointer" />
					</template>
					<div class="flex flex-col gap-xs p-xs min-w-[200px]">
						<PiDropdownItem
							v-for="option in sortOptions"
							:key="option.value"
							:label="option.label"
							:right-icon="option.value === piecesStore.sortBy ? markRaw(PiIconSolidCheck) : null"
							right-icon-color="primary"
							@click="handleSortChange(option.value)"
						/>
					</div>
				</PiDropdown>
			</div>
		</div>

		<!-- Loading -->
		<div v-if="piecesStore.isLoading" class="flex items-center justify-center p-8">
			<PiSpinner />
		</div>

		<!-- Erreur -->
		<PiMessage
			v-else-if="piecesStore.error"
			type="danger"
			:label="piecesStore.error"
			link-label="Réessayer"
			size="sm"
			has-close-button
			@link-alert="loadPieces"
			@close-alert="piecesStore.error = null"
		/>

		<!-- Liste vide -->
		<div v-else-if="piecesStore.filteredPieces.length === 0" class="flex flex-col items-center p-8 text-center">
			<p class="text-sm text-neutral-500">Aucune pièce trouvée</p>
			<a class="text-xs text-primary-500 hover:underline mt-2 cursor-pointer" @click="openExternal(addPiecesUrl)">
				Ajouter des pièces
			</a>
		</div>

		<!-- Liste des pièces -->
		<div v-else class="flex-1 overflow-y-auto px-3 pb-3">
			<PieceCard
				v-for="piece in piecesStore.filteredPieces"
				:key="piece.id"
				:piece="piece"
				:is-inserted="piecesStore.isInserted(piece.id)"
				:number="piecesStore.getNumber(piece.id)"
				:occurrence-count="piecesStore.getOccurrenceCount(piece.id)"
				@click="handleScrollToPiece(piece.id)"
				@insert="handleInsertPiece(piece)"
			/>
			<div class="flex justify-center mt-2">
				<PiButton
					label="Ajouter des pièces"
					color="primary"
					type="ghost"
					size="sm"
					@click="openExternal(addPiecesUrl)"
				/>
			</div>
		</div>

		<!-- Modale de chargement rappel des faits -->
		<div v-if="isGenerating" class="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
			<div class="bg-white rounded-lg p-6 flex flex-col items-center gap-3 shadow-lg">
				<PiSpinner size="lg" />
				<p class="text-sm text-neutral-700">
					Génération en cours du rappel des faits, cela peut prendre jusqu'à une minute
				</p>
			</div>
		</div>
	</div>
</template>
